import { Injectable } from '@angular/core';
import {
  CUSTOMER_ACTIONS,
  DotCmsClient,
  initEditor,
  isInsideEditor,
  postMessageToEditor,
  updateNavigation,
} from '@dotcms/client';
import { Store, select } from '@ngrx/store';
import {
  Observable,
  combineLatest,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs';

import { AppFacade } from 'ish-core/facades/app.facade';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import { selectCleanUrl, selectPreviousUrl, selectQueryParams } from 'ish-core/store/core/router';
import { isServerConfigurationLoaded } from 'ish-core/store/core/server-config';
import { whenTruthy } from 'ish-core/utils/operators';

import { dotCmsBlogSlugRegex } from '../configurations/dot-cms-regex';
import { CustomDotCmsPageContainer } from '../models/container/custom-dot-cms-page-container.model';
import { CustomDotCmsContentlet } from '../models/contentlet/custom-dotcms-contentlet.model';
import { CustomDotCmsPage, CustomDotCmsPageAsset } from '../models/page/custom-dot-cms-page.model';
import {
  DotCmsContentDepth,
  DotCmsNavigationDepth,
  DotCmsPageRequestParameters,
} from '../services/dot-cms-resource/dot-cms-resource.service';
import { getDotCmsState } from '../store/dot-cms-store';
import { loadDotCmsConfigurableContent } from '../store/dotcms-configurable-contents/dotcms-configurable-contents.action';
import { getConfigurableContentletIdentifier } from '../store/dotcms-configurable-contents/dotcms-configurable-contents.selectors';
import {
  setDotCmsLanguagePreference,
  setDotCmsPersona,
} from '../store/dotcms-configuration/dotcms-configuration.action';
import {
  getDotCmsApiEndpoint,
  getDotCmsApitoken,
  getDotCmsAvailableLanguages,
  getDotCmsPersonaId,
  isDotCmsCanMakeApiCall,
} from '../store/dotcms-configuration/dotcms-configuration.selectors';
import { getDotCmsContainerByIdentifier } from '../store/dotcms-container/dotcms-container-selectors';
import { getDotCmsContentletByIdentifier } from '../store/dotcms-contentlets/dotcms-contentlet.selectors';
import { loadDotCmsContentlet } from '../store/dotcms-contentlets/dotcms-contentlets.action';
import {
  loadDotCmsHeaderNavigation,
  triggerDotcmsLoading,
  updateUVENavigation,
} from '../store/dotcms-navigation/dotcms-navigation.action';
import { getDotCmsHeaderNavigationItem } from '../store/dotcms-navigation/dotcms-navigation.selectors';
import { getDotCmsPageByURI } from '../store/dotcms-pages/dotcms-page.selector';
import { loadDotCmsPage, loadUVEDotCmsPageData } from '../store/dotcms-pages/dotcms-pages.action';
import { generateDotCmsRouterURI } from '../utils/dot-cms-utils';

/* eslint-disable @typescript-eslint/member-ordering */
@Injectable({ providedIn: 'root' })
export class DotCmsFacade {
  DOT_CMS_CLIENT: DotCmsClient;
  constructor(private store: Store, private appFacade: AppFacade) {}

  dotCmsState$ = this.store.pipe(select(getDotCmsState));

  getDotCmsHost$ = this.store.pipe(select(getDotCmsApiEndpoint));

  getSelectedPageUrl$ = this.store.pipe(select(selectCleanUrl));

  isDotCmsReadyForApiCall$ = this.store.pipe(select(isDotCmsCanMakeApiCall));

  getApiToken$ = this.store.pipe(select(getDotCmsApitoken));

  getQueryParams$ = this.store.pipe(select(selectQueryParams), whenTruthy()).pipe(take(1));

  getPageByURI$(
    pageURI: string,
    params?: DotCmsPageRequestParameters,
    dispatch?: boolean
  ): Observable<CustomDotCmsPage> {
    if (dispatch && !isInsideEditor()) {
      this.store.dispatch(loadDotCmsPage({ pageURI, params }));
    }
    return this.store.pipe(select(getDotCmsPageByURI(pageURI)), whenTruthy());
  }

  getContainerByIdentifier$(identifier: string): Observable<CustomDotCmsPageContainer> {
    //Todo: Dispatch an optional action for make api call
    return this.store.pipe(select(getDotCmsContainerByIdentifier(identifier)), whenTruthy(), distinctUntilChanged());
  }

  getContentletByIdentifier$(
    identifier: string,
    depth?: DotCmsContentDepth,
    dispatch?: boolean
  ): Observable<CustomDotCmsContentlet> {
    if (dispatch) {
      this.store.dispatch(loadDotCmsContentlet({ identifier, depth }));
    }

    return this.store.pipe(select(getDotCmsContentletByIdentifier(identifier)), whenTruthy());
  }

  getConfigurableContentletByKey$(contentKey: string) {
    this.store.dispatch(loadDotCmsConfigurableContent({ contentKey }));
    return this.store.pipe(
      select(getConfigurableContentletIdentifier(contentKey)),
      switchMap(identifier => this.getContentletByIdentifier$(identifier))
    );
  }

  getHeaderNavigation$(depth?: DotCmsNavigationDepth, dispatch?: boolean) {
    if (dispatch) {
      this.store.dispatch(loadDotCmsHeaderNavigation({ depth }));
    }
    return this.store.pipe(select(getDotCmsHeaderNavigationItem), whenTruthy());
  }

  getFooterContentlet$() {
    return this.store.pipe(
      select(isServerConfigurationLoaded),
      filter(loaded => loaded),
      debounceTime(10),
      concatMap(() => this.getConfigurableContentletByKey$('footerIdentifier'))
    );
  }

  fetchHeaderLinks$() {
    return this.store.pipe(
      select(isServerConfigurationLoaded),
      filter(loaded => loaded),
      debounceTime(10),
      take(1),
      concatMap(() => this.getHeaderNavigation$(5, true))
    );
  }

  getPersonaId$(): Observable<string> {
    return this.store.pipe(select(getDotCmsPersonaId));
  }
  getAvailableLanguages$() {
    return this.store.pipe(select(getDotCmsAvailableLanguages));
  }

  setDotCmsPreferredLanguage(language: number) {
    this.store.dispatch(setDotCmsLanguagePreference({ language }));
  }

  setPersona(personaId: string) {
    this.store.dispatch(setDotCmsPersona({ personaId }));
  }

  initEditMode(uri: string) {
    this.setDotCMSClientEnvironment$();
    initEditor({ pathname: uri });
    updateNavigation(uri);
  }
  //This method is used for DotCms UVE editor navigation.
  emitUveNavUrl() {
    this.getSelectedPageUrl$
      .pipe(
        takeWhile(() => isInsideEditor()),
        take(1),
        map(url => generateDotCmsRouterURI(url))
      )
      .subscribe(url => {
        this.navigateInEditor(url);
      });
  }

  navigateInEditor(pathname: string) {
    this.store.dispatch(updateUVENavigation({ pathname }));
    this.store.dispatch(triggerDotcmsLoading());
  }
  destroyEditor() {
    this.DOT_CMS_CLIENT.editor.off('changes');
  }

  listeningChangeEventInUVEEditor() {
    this.DOT_CMS_CLIENT.editor.on('changes', (pageAsset: CustomDotCmsPageAsset) => {
      this.setDotCmsPreferredLanguage(pageAsset.viewAs.language.id);
      this.store.dispatch(loadUVEDotCmsPageData({ page: pageAsset }));
      this.getConfigurableContentletByKey$('footerIdentifier');
    });
  }
  setDotCMSClientEnvironment$(): Observable<boolean> {
    return combineLatest([this.getApiToken$, this.getDotCmsHost$, this.getSelectedPageUrl$]).pipe(
      filter(([token, endPoint]) => token !== undefined && endPoint !== undefined),
      whenTruthy(),
      distinctUntilChanged(),
      tap(([token, endpoint, url]) => {
        this.DOT_CMS_CLIENT = DotCmsClient.init({
          dotcmsUrl: endpoint,
          authToken: token,
        });
        this.listeningChangeEventInUVEEditor();

        if (!SSR) {
          postMessageToEditor({
            action: CUSTOMER_ACTIONS.CLIENT_READY,
            payload: { params: { depth: dotCmsBlogSlugRegex.test(url) ? 3 : 2 } },
          });
        }
      }),
      map(([token, endPoint]) => !!token && !!endPoint)
    );
  }

  generateImageURIFromImageKey(contentletIdentifier: string, imageKey: string): Observable<string> {
    return combineLatest([
      this.getDotCmsHost$.pipe(whenTruthy()),
      this.getContentletByIdentifier$(contentletIdentifier).pipe(whenTruthy()),
    ]).pipe(
      whenTruthy(),
      filter(([, contentlet]) => this.isString(contentlet[imageKey as keyof CustomDotCmsContentlet])),
      map(
        ([host, contentlet]) =>
          `${host + contentlet[imageKey as keyof CustomDotCmsContentlet]}/v/${contentlet.inode}?language_id=${
            contentlet.languageId
          }`
      )
    );
  }

  generateVideoURIFromImageKey(contentletIdentifier: string, videokey: string): Observable<string> {
    const setThumbnail = '#t=0.01'; //sets thumbnail by getting the frame of the video at 0.01 th second
    return combineLatest([
      this.getDotCmsHost$.pipe(whenTruthy()),
      this.getContentletByIdentifier$(contentletIdentifier).pipe(whenTruthy()),
    ]).pipe(
      whenTruthy(),
      //@ts-ignore
      filter(([, contentlet]) => this.isString(contentlet[videokey])),
      //@ts-ignore
      map(([host, contentlet]) => `${host + contentlet[videokey]}?language_id=${contentlet.languageId + setThumbnail}`)
    );
  }

  getBannerImageURI(contentlet: CustomDotCmsContentlet): Observable<string> {
    return this.appFacade.deviceType$.pipe(
      mergeMap(deviceType => {
        const imageKey = this.generateImageKeyForBannerUsingDeviceType(contentlet, deviceType);
        return this.generateImageURIFromImageKey(contentlet.identifier, imageKey);
      })
    );
  }

  getVideoHeight(contentlet: CustomDotCmsContentlet): Observable<string> {
    return this.appFacade.deviceType$.pipe(
      map(deviceType => this.generateVideoHeightUsingDeviceType(contentlet, deviceType))
    );
  }

  getVideoClasses(contentlet: CustomDotCmsContentlet): string {
    if (contentlet?.maxWidth) {
      return `${contentlet.cssClass}` + ` video-max-width`;
    }

    return contentlet.cssClass;
  }

  private generateVideoHeightUsingDeviceType(contentlet: CustomDotCmsContentlet, deviceType: DeviceType) {
    return deviceType === 'desktop'
      ? contentlet.heightlg
      : deviceType === 'tablet'
      ? contentlet.heightmd
      : deviceType === 'mobile'
      ? contentlet.heightsm
      : '';
  }

  private generateImageKeyForBannerUsingDeviceType(contentlet: CustomDotCmsContentlet, deviceType: DeviceType) {
    const imageSize =
      deviceType === 'desktop'
        ? 'lg'
        : deviceType === 'tablet'
        ? this.isString(contentlet.imagemd)
          ? 'md'
          : 'lg'
        : deviceType === 'mobile'
        ? this.isString(contentlet.imagesm)
          ? 'sm'
          : this.isString(contentlet.imagemd)
          ? 'md'
          : 'lg'
        : '';

    return `image${imageSize}`;
  }

  private isString(object: {}) {
    return typeof object === 'string';
  }

  getPreviousUrl(): Observable<string> {
    return this.store.pipe(select(selectPreviousUrl));
  }
}
